import type {
  ProductCreateRequest,
  ProductListQuery,
  ProductPatchRequest,
  ProductReorderRequest,
  ProductUsageDetail,
} from '@shared/types/product';
import type {
  ProductProblemLink,
  ProductProblemLinkList,
  ProductProblemLinkUpsertRequest,
} from '@shared/types/product-problem-link';
import type {
  ProductCompetitorService,
  ProductCompetitorServiceCreateRequest,
  ProductCompetitorServiceList,
  ProductCompetitorServiceRelinkRequest,
  ProductCompetitorServiceUpdateRequest,
  UnlinkedProductCompetitorServiceList,
} from '@shared/types/product-competitor-service';
import { ApiError, api } from './client';

function buildQueryString(query: ProductListQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs.length === 0 ? '' : `?${qs}`;
}

export function listProducts(query: ProductListQuery = {}): Promise<unknown> {
  return api.get(`/api/master/products${buildQueryString(query)}`);
}

export function getProduct(id: string): Promise<unknown> {
  return api.get(`/api/master/products/${encodeURIComponent(id)}`);
}

export function createProduct(body: ProductCreateRequest): Promise<unknown> {
  return api.post('/api/master/products', body);
}

export function patchProduct(
  id: string,
  body: ProductPatchRequest,
): Promise<unknown> {
  return api.patch(`/api/master/products/${encodeURIComponent(id)}`, body);
}

export async function reorderProducts(
  body: ProductReorderRequest,
): Promise<void> {
  return api.put<void>('/api/master/products/order', body);
}

export async function deleteProduct(id: string): Promise<void> {
  return api.delete(`/api/master/products/${encodeURIComponent(id)}`);
}

export async function getProductUsage(id: string): Promise<ProductUsageDetail> {
  return api.get<ProductUsageDetail>(
    `/api/master/products/${encodeURIComponent(id)}/usage`,
  );
}

export async function listProductProblemLinks(
  productId: string,
): Promise<ProductProblemLinkList> {
  return api.get<ProductProblemLinkList>(
    `/api/master/products/${encodeURIComponent(productId)}/problem-links`,
  );
}

export async function upsertProductProblemLink(
  productId: string,
  problemId: string,
  body: ProductProblemLinkUpsertRequest,
): Promise<ProductProblemLink> {
  return api.put<ProductProblemLink>(
    `/api/master/products/${encodeURIComponent(productId)}/problem-links/${encodeURIComponent(problemId)}`,
    body,
  );
}

export async function deleteProductProblemLink(
  productId: string,
  problemId: string,
): Promise<void> {
  return api.delete(
    `/api/master/products/${encodeURIComponent(productId)}/problem-links/${encodeURIComponent(problemId)}`,
  );
}

export async function listProductCompetitorServices(
  productId: string,
): Promise<ProductCompetitorServiceList> {
  return api.get<ProductCompetitorServiceList>(
    `/api/master/products/${encodeURIComponent(productId)}/competitors`,
  );
}

export async function listUnlinkedProductCompetitorServices(): Promise<
  UnlinkedProductCompetitorServiceList
> {
  return api.get<UnlinkedProductCompetitorServiceList>(
    '/api/master/products/competitors/unlinked',
  );
}

export async function createProductCompetitorService(
  productId: string,
  body: ProductCompetitorServiceCreateRequest,
  legacyTitle: string,
): Promise<ProductCompetitorService> {
  const url = `/api/master/products/${encodeURIComponent(productId)}/competitors`;
  try {
    return await api.post<ProductCompetitorService>(url, body);
  } catch (error) {
    const oldBackendRejectedNewField =
      error instanceof ApiError &&
      error.status === 400 &&
      error.message.includes('unknown field') &&
      error.message.includes('competitor_service_id');
    if (!oldBackendRejectedNewField) throw error;

    // Rolling-deploy compatibility only: the old backend accepts free-text
    // title cards and rejects competitor_service_id as an unknown field.
    return api.post<ProductCompetitorService>(url, {
      title: legacyTitle,
      ...(body.memo === undefined ? {} : { memo: body.memo }),
    });
  }
}

export async function patchProductCompetitorService(
  productId: string,
  competitorId: string,
  body: ProductCompetitorServiceUpdateRequest,
): Promise<ProductCompetitorService> {
  return api.patch<ProductCompetitorService>(
    `/api/master/products/${encodeURIComponent(productId)}/competitors/${encodeURIComponent(competitorId)}`,
    body,
  );
}

export async function relinkProductCompetitorService(
  productId: string,
  competitorId: string,
  body: ProductCompetitorServiceRelinkRequest,
): Promise<ProductCompetitorService> {
  return api.patch<ProductCompetitorService>(
    `/api/master/products/${encodeURIComponent(productId)}/competitors/${encodeURIComponent(competitorId)}`,
    body,
  );
}

export async function deleteProductCompetitorService(
  productId: string,
  competitorId: string,
): Promise<void> {
  return api.delete(
    `/api/master/products/${encodeURIComponent(productId)}/competitors/${encodeURIComponent(competitorId)}`,
  );
}
